import classNames from 'classnames';
import {animate, motion, useMotionValue} from 'framer-motion';
import {ForwardedRef, forwardRef, useImperativeHandle, useRef} from 'react';
import styles from './LoadingBar.module.css';

interface IProps {
    className?: string;
    id?: string;
}

type LoadingBar = {
    getProgress: () => number;
    setProgress: (progress: number) => void;
    reset: () => void;
};

const LoadingBar = forwardRef(function LoadingBar(
    {className, id}: IProps,
    ref: ForwardedRef<LoadingBar>,
) {
    const opacity = useMotionValue(0);
    const width = useMotionValue('0%');
    const lastProgress = useRef(0);

    useImperativeHandle(ref, () => ({
        reset: () => {
            opacity.stop();
            width.stop();
            opacity.set(1);
            width.set('0%');
        },
        setProgress: progress => {
            opacity.stop();
            width.stop();
            opacity.set(1);
            if (lastProgress.current === 1) {
                width.set('0%');
            }
            lastProgress.current = progress;
            if (progress < 1) {
                animate(width, 100 * progress + '%', {
                    duration: 3,
                });
            } else if (progress === 1) {
                animate(width, '100%', {
                    duration: 0.5,
                    onComplete: () => {
                        animate(opacity, 0, {duration: 0.3});
                    },
                });
            }
        },
        getProgress: () => lastProgress.current,
    }));

    return (
        <motion.div
            id={id}
            className={classNames(styles.bar, className)}
            style={{opacity, width}}
        />
    );
});

export default LoadingBar;
